import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import Select from '@/components/Select';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { editorsPick } from '@/services/editorsPick/editorsPick.types';
import { useMemo } from 'react';
import { EditorPickForm } from '../form/EditorsPickForm';
import useDeleteEditorsPick from '../hooks/useDeleteEditorsPick';
import { getColumns } from './Column';

export function EditorsPickTable({
  location,
  onLocationChange,
  data,
  pagination,
  loading,
}: {
  location: string;
  onLocationChange: (value: string) => void;
  data: editorsPick[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { t } = useDynamicLocale();
  const { data: locationOptions = [] } = useAttributes('location');
  const addNew = useCan({ allOf: [PERMS.recommendation.editorPick.create] });
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsyncDeleteEditorsPick } = useDeleteEditorsPick();
  const columns = useMemo(
    () => getColumns(openConfirmation, onOpen, t, mutateAsyncDeleteEditorsPick),
    [openConfirmation, onOpen, t, mutateAsyncDeleteEditorsPick],
  );

  const pageCount = useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const handleAddNew = () => {
    onOpen({
      title: 'Manage Editors Pick',
      modalTitle: 'common.page.modal.manageEditorsPickInfo.title',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      component: <EditorPickForm mode="add" location={location} />,
    });
  };
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'editorPickSno', desc: true }],
      pagination: { pageIndex: 0, pageSize: 5 },
    },
    meta: {
      title: <Translation id="recommendationManagement.editorsPick.table.title" />,
      totalPage: pagination.totalItems,
      loading,
      actionKey: addNew ? 'onAddNew' : '',
      action: addNew ? handleAddNew : null,
    },
    getRowId: (originalRow) => String(originalRow.editorPickSno),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <div className="relative">
      <div className="absolute right-0 -top-[65px] flex gap-3">
        <Select
          name="Location"
          options={locationOptions}
          triggerLabel={t('common.form.label.editorsPickLocation')}
          labelInTrigger={true}
          value={location}
          onValueChange={onLocationChange}
          triggerClassName="flex gap-2 font-bold rounded border shadow-control border-primary text-copy leading-[24px] px-4 py-3"
        />
      </div>
      <DataTable table={table} className="font-medium">
        <DataTableToolbar />
      </DataTable>
    </div>
  );
}
